import { Alert, Button, Flex, Input, Select } from "antd";
import React, { FC, useContext, useEffect, useState } from "react";
import { useViewerStore } from "../../../../../store/store";
import siteService from "../../../../../services/siteService";
import { getDesignVersionList } from "../../../../../services/designVersionService";
import { useCurrentUser } from "../../../../../contexts/UserContext";
import { CaretDown } from "@phosphor-icons/react";
import {
    ACCESS_MULTIPLIERS,
    FACE_IDEAL_API,
    MODULE_GRAPH,
    SHADE_CALCULATIONS,
    siteCalculationState,
} from "../../../../../services/siteCalculationState";
import { uploadToBox } from "../../../../../services/documentService";
import { LoadingContext } from "../../../../../store/loadingMessageStore";
import { generateDirectLayout } from "../../Drawer/Drawer-Sections/5-Reports-Section/Reports/Direct_Layout";
import { generateMeasureLayout } from "../../Drawer/Drawer-Sections/5-Reports-Section/Reports/Measure_Layout";
import { generateSiteReport } from "../../Drawer/Drawer-Sections/5-Reports-Section/Reports/Site_Report";
import { isBoxUploadEnabled } from "../../../../../helpers/featureFlags";
import { logger } from "../../../../../services/loggingService";
import { UnitContext } from "../../../../../contexts/UnitContext";
import treeEPCService from "../../../../../services/treeEPCService";
import { useCalculationStates } from "../../../../../hooks/calculationStateHook";
import { useGetSaveDesignData } from "../../../../../hooks/useGetSaveDesignData";
import { ExportGen } from "../../Three-JS-Viewer/Controllers/Common";
import { uploadCustomMap } from "../../Three-JS-Viewer/Controllers/MapControl";
import ProductionValuesValidation from "./ProductionValuesValidation";
import {CalculationType, ImageryOptions} from "../../../../../@types/enums";
import {Buffer} from 'buffer';
window.Buffer = window.Buffer || Buffer

export interface ValidationError {
    roofID: string | number;
    field: "efficiency" | "productionActual" | "productionUtil" | "solarAccess";
    badValue: unknown;
}

interface ProductionValues {
    roofID: string | number;
    efficiency: number;
    productionActual: number;
    productionUtil: number;
    solarAccess: number;
}


const FinaliseConfirm: FC = () => {
    const unitContext = useContext(UnitContext);
    const { hasCapability } = useCurrentUser();

    const [saving, setSaving] = useState<boolean>(false);
    const [rsaStatus, setRsaStatus] = useState<string | undefined>();
    const [annualUsageInput, setAnnualUsageInput] = useState<number | null>(null);
    const [epcEstimatedProductionInput, setEpcEstimatedProductionInput] =
        useState<number | null>(null);

    const {
        sceneViewer,
        designID,
        designList,
        isInActiveDesign,
        setbackNotes,
        shadeCalculatingIdx,
        shadeCalculatingLength,
        annualUsage,
        epcEstimatedProduction,
        imageryType,
        customImageFile,
        refreshingShade,
        productType,
        detachedStructureBuildingIds
    } = useViewerStore((state) => state);

    const isOpportunityRRorRRR = productType.includes('RRR') || productType.includes('R&R');

    const { needsUpdate } = useCalculationStates(
        [FACE_IDEAL_API, MODULE_GRAPH, SHADE_CALCULATIONS, ACCESS_MULTIPLIERS] as CalculationType[],
        { useCalculation: true }
    );
    const notReady: boolean = Object.values(needsUpdate).includes(true) || refreshingShade;

    useEffect(() => {
        siteCalculationState.needsUpdate(SHADE_CALCULATIONS as CalculationType);
        siteCalculationState.useCalculations(SHADE_CALCULATIONS as CalculationType);
    }, []);

    useEffect(() => {
        if (annualUsage) {
            setAnnualUsageInput(annualUsage);
            return;
        }
        if (epcEstimatedProduction) {
            setEpcEstimatedProductionInput(epcEstimatedProduction);
            return;
        }

        const salesforceId = sessionStorage.getItem("SalesForceId");
        treeEPCService
            .getOpportunityStatusForEPCPricing(salesforceId as string)
            .then((epc) => useViewerStore.setState({ annualUsage: epc?.UsageValue ?? 0 }));
    }, [annualUsage, epcEstimatedProduction]);

    const getPayload = useGetSaveDesignData(rsaStatus, annualUsageInput as number);

    const rsaStatusOptions = [
        { label: "RSA Complete", value: "RSA Complete" },
        { label: "RSA: Estimate Shade", value: "RSA: Estimate Shade" },
        { label: "RSA: Estimated Layout", value: "RSA: Estimated Layout" },
        { label: "RSA: Full Estimate", value: "RSA: Full Estimate" },
        { label: "Needs Measure", value: "Needs Measure" },
        { label: "EV Not Available", value: "EV Not Available" },
        { label: "RSA Not Viable", value: "RSA Not Viable" },
        { label: "Unable to ID Home", value: "Unable to ID Home" },
        { label: "Aurora Estimate", value: "Aurora Estimate" },
        { label: "Aurora Complete", value: "Aurora Complete" },
        { label: "oneDRAW Estimate", value: "oneDRAW Estimate" },
        { label: "oneDRAW Complete", value: "oneDRAW Complete" },
    ];


    const validateProductionValues = (
        payload: Awaited<ReturnType<typeof getPayload>>,
    ): { goodRoofs: ProductionValues[]; errors: ValidationError[] } => {
        const goodRoofs: ProductionValues[] = [];
        const errors: ValidationError[] = [];

        const { designDetails } = payload;
        const { buildingDetails } = designDetails[0] ?? {};

        if (!buildingDetails?.length) {
            return {
                goodRoofs,
                errors: [
                    { roofID: "N/A", field: "efficiency", badValue: "No building details" },
                ],
            };
        }

        buildingDetails.forEach(({ roofDetails }: any) => {
            roofDetails.forEach((roof: any) => {
                const { effic, prodActual, prodUtil, solAcc, tag_Point: roofID } = roof;

                const checks: Array<[ValidationError["field"], unknown]> = [
                    ["efficiency", effic],
                    ["productionActual", prodActual],
                    ["productionUtil", prodUtil],
                    ["solarAccess", solAcc],
                ];

                let roofIsValid = true;
                checks.forEach(([field, value]) => {
                    if (value === undefined || value === null || Number.isNaN(value)) {
                        roofIsValid = false;
                        errors.push({ roofID, field, badValue: value });
                    }
                });

                if (roofIsValid) {
                    goodRoofs.push({
                        roofID,
                        efficiency: effic,
                        productionActual: prodActual,
                        productionUtil: prodUtil,
                        solarAccess: solAcc,
                    });
                }
            });
        });

        return { goodRoofs, errors };
    };

    function showEfficiencyOverridePrompt(): Promise<boolean> {
        return new Promise((resolve) => {
            sceneViewer.viewerAlert({
                show: true,
                title: "Efficiency Validation Error",
                message: (
                    <>
                        <p style={{marginBottom: '1rem'}}>Efficiency is minimally greater than solar access. Would you like to override the efficiency to match the solar access and continue finalization?</p>
                        <p><strong>Efficiency: </strong>{useViewerStore.getState().efficiency}</p>
                        <p><strong>Solar Access: </strong>{useViewerStore.getState().weightedSolarAverage}</p>
                        <Alert 
                            style={{color: 'black', fontSize: '12px', marginTop: '1rem'}}
                            message="Pressing Confirm will continue the finalization process, while pressing Cancel will stop it."
                            type='warning'
                            showIcon
                        />
                        <div style={{ display: "flex", gap: "10px", marginTop: "1rem" }}>
                            <Button
                                size="middle"
                                type="primary"
                                className="editor__control--finalize modal-confirm-button"
                                onClick={() => {
                                    useViewerStore.setState({efficiencyOverride: useViewerStore.getState().weightedSolarAverage as number})
                                    sceneViewer.viewerAlert({ show: false });
                                    resolve(true); // Confirm
                                }}
                            >
                                Confirm
                            </Button>
                            <Button
                                size="middle"
                                type="primary"
                                className="editor__control--finalize modal-cancel-button"
                                onClick={() => {
                                    sceneViewer.viewerAlert({ show: false });
                                    resolve(false); // Cancel
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                ),
                messageType: "warning",
                isModal: true,
            });
        });
    }

    const saveDesignHandler = async (): Promise<void> => {
        const salesForceID = sessionStorage.getItem("SalesForceId");
        const totalSystemSize = useViewerStore.getState().totalSystemSize as number | undefined;

        // Validate total system size before proceeding
        if (!isOpportunityRRorRRR && (!totalSystemSize || Number.isNaN(totalSystemSize) || totalSystemSize === 0)) {
            logger.error("Finalization Blocked : Total system size is invalid:", totalSystemSize);
            sceneViewer.viewerAlert({
                show: true,
                title: "Production Data Validation Error",
                message: (
                    <ProductionValuesValidation errors={["Total System Size is  0 , Empty Module Layout"]} />
                ),
                messageType: "error",
                isModal: true,
            });
            return;
        }

        const payload = await getPayload();
        const data  = await siteService.saveSiteDetails(payload);

        const { goodRoofs: productionValuesArray, errors } = validateProductionValues(payload);

        if (errors.length) {
            errors.forEach((e) =>
                logger.error(
                    `Finalization Blocked : Roof ${e.roofID} ► ${e.field} is invalid (value: ${String(
                        e.badValue,
                    )})`,
                    {},
                ),
            );

            const errorMessages = errors.map(
                (e) => `Roof ${e.roofID}: ${e.field} is invalid (value: ${String(e.badValue) || "null/undefined"})`,
            );

            sceneViewer.viewerAlert({
                show: true,
                title: "Production Data Validation Error",
                message: <ProductionValuesValidation errors={errorMessages} />,
                messageType: "error",
                isModal: true,
            });
            return;
        }

        if (data?.Message === "Error occured during the saving of records. Contact Administrator") {
            sceneViewer.viewerAlert({
                show: true,
                title: "Save Failed",
                message: "Error occured during the saving of records. Contact Administrator",
                messageType: "error",
                isModal: false,
            });
            return;
        }

        logger.info(data, "saved design data");
        const DesignNumber = data.design[0]?.Design_No;
        const VersionNumber = data.design[0]?.Version_No;

        if (
            designList &&
            designList[0]?.Design_Certification_Status &&
            !["Not Certified", "Certified Design Override", null].includes(
                designList[0]?.Design_Certification_Status,
            )
        ) {
            await siteService.saveDesignCertification(data.design[0].Id, 4);
        }

        useViewerStore.setState({
            designID: data.design[0]?.Id,
            isInActiveDesign: false,
            isSavingInProgress: false,
        });

        const designListNew = await getDesignVersionList(salesForceID as string);
        if (DesignNumber && VersionNumber) saveEpcEstimate(DesignNumber, VersionNumber);
        if (imageryType === ImageryOptions.CUSTOM && customImageFile)
            uploadCustomMap(customImageFile);

        if (designListNew) {
            useViewerStore.setState({designList : designListNew});
            useViewerStore.setState({selectedDesignIndex : 0})
            useViewerStore.setState({selectedSiteDesign : null})
        }

        if (isBoxUploadEnabled() || salesForceID === "006Pl000009urMIIAY") {
            sceneViewer.viewerAlert({ show: false });
            const files = await generateAllReports(annualUsageInput, epcEstimatedProductionInput);
            if (files.length > 0) {
                await uploadAllFiles(
                    salesForceID,
                    files,
                    // On Upload Complete
                    (uploadedFiles, notUploaded) => {
                        if (uploadedFiles.length === files.length) {
                            sceneViewer.viewerAlert({
                                show: true,
                                title: "Design Saved & All Files Uploaded to Box",
                                message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\n Files uploaded:\n ${uploadedFiles.join("\n")}`,
                                messageType: "success",
                                isModal: false,
                            });
                        } else {
                            sceneViewer.viewerAlert({
                                show: true,
                                title: "Design Saved & Failed to Uploaded Some Files",
                                message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\n Unable to upload files : ${notUploaded.join("\n")}`,
                                messageType: "error",
                                isModal: false,
                            });
                        }
                    },
                    // On uploading error
                    (error) => {
                        sceneViewer.viewerAlert({
                            show: true,
                            title: "Design Saved & Error while Uploading Files",
                            message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}\n\n ${error}`,
                            messageType: "error",
                            isModal: false,
                        });
                    },
                );
            }
        } else {
            sceneViewer.viewerAlert({
                show: true,
                title: "Design Saved Successfully",
                message: `Design Number : ${DesignNumber} & Version Number : ${VersionNumber}`,
                messageType: "success",
                isModal: false,
            });
        }
    };


    const confirm = async (): Promise<void> => {
        const solarAccess = useViewerStore.getState().weightedSolarAverage as number;
        const efficiency = useViewerStore.getState().efficiency;
        useViewerStore.setState({efficiencyOverride: null});
        const acceptableEfficiencyOverrideThreshold = 3;
        // If user has DL permission and leaves it blank, set to null so SF is not overridden
        if (hasCapability("Direct_Layout")) {
            if (!annualUsageInput) setAnnualUsageInput(null);
            if (!epcEstimatedProductionInput) setEpcEstimatedProductionInput(null);
        } else {
            // User doesn't have DL permission, ensure both values are null
            setAnnualUsageInput(null);
            setEpcEstimatedProductionInput(null);
        }

        if (efficiency > solarAccess) {
            if (efficiency - solarAccess <= acceptableEfficiencyOverrideThreshold && hasCapability("Efficiency_Override")) {
                const userConfirmed = await showEfficiencyOverridePrompt();
                if (!userConfirmed) {
                    return; // User clicked Cancel
                }
            } else {
                logger.error(
                    "Finalization Blocked: Efficiency is greater than solar access",
                    "Efficiency: " + efficiency,
                    "Solar Access: " + solarAccess
                );
                sceneViewer.viewerAlert({
                    show: true,
                    title: "Efficiency Validation Error",
                    message: (
                        <ProductionValuesValidation errors={["Efficiency is greater than solar access. Please contact the RSA team for assistance."]} />
                    ),
                    messageType: "error",
                    isModal: true,
                });
                return;
            }
        }

        sceneViewer.enableAllLayers();
        useViewerStore.setState({ isSavingInProgress: true });
        setSaving(true);
        sceneViewer.viewerAlert({ show: true, isModal: true, title: "Saving Design",
            message: 
                <>
                    <Alert
                        style={{ color: "black" }}
                        message="This may take up to 30 seconds, don't refresh or quit while in progress."
                        type="info"
                        showIcon
                    />
                    <br />
                    <Flex style={{ textAlign: "center" }} gap={10} align="center" justify="center">
                        <span className="loader-icon-spinner " />
                        <p className="theme-based-text-color">
                            Saving New {designID === 0 || isInActiveDesign ? "Design" : "Version"} to the database. Please Wait...
                        </p>
                    </Flex>
                </>
        });

        try {
            await saveDesignHandler();
        } catch (ex: any) {
            logger.error(ex);
            let errorMessagesArray: string[] = [];
            let errorMessagesString = "";
            if (ex.response?.data?.Errors && Array.isArray(ex.response.data.Errors)) {
                errorMessagesArray = ex.response.data.Errors.map((err: any) => err.ErrorMessage);
                errorMessagesString = `\n\nDetails: ${errorMessagesArray.join("; ")}`;
            }
            let message = `Failed to Save: ${ ex.message + " " + errorMessagesString}`;

            /** Verbose Error extraction */
            const verboseErrorMessage = true;
            if (verboseErrorMessage) {
                try {
                    const errors = ex.response.data.errors;
                    if (errors) {
                        for (const key of Object.keys(errors)) {
                            message += `\n${key}: ${errors[key]}`;
                        }
                    }
                } catch {
                    /* ignore */
                }

                try {
                    message += `\n${ex.response.data.Message}`;
                } catch {
                    /* ignore */
                }
            }

            sceneViewer.viewerAlert({ show: false });
            useViewerStore.setState({ isSavingInProgress: false });
            sceneViewer.viewerAlert({
                show: true,
                title: "Error",
                message,
                messageType: "error",
                isModal: false,
            });
        }
    };

    const cancel = (): void => {
        sceneViewer.viewerAlert({ show: false });
    };


    const generateAllReports = async (
        annualUsageVal: number | null,
        productionVal: number | null,
    ) => {
        const viewerState = useViewerStore.getState();
        const { siteAddress } = viewerState;
        const loadingContext = new LoadingContext();
        loadingContext.setDefaultTitle("Generating Reports...");
        const files: Array<{ data: Blob; name: string }> = [];

        try {
            /** Direct Layout */
            if (hasCapability("Direct_Layout")) {
                loadingContext.setMessage({ title: "Generating Files", message: "Generating Direct Layout..." });
                const directLayoutPdf = await generateDirectLayout(viewerState, annualUsageVal, productionVal);
                if (directLayoutPdf) {
                    const fileName = `${siteAddress} - DIRECT LAYOUT.pdf`;

                    const buffer = directLayoutPdf.output("arraybuffer") as ArrayBuffer;

                    const pdfBlob = new Blob([buffer], { type: "application/pdf" });

                    files.push({ data: pdfBlob, name: fileName });
                }
            }

            /** Measure Layout */
            if (hasCapability("Measure_Layout")) {
                loadingContext.setMessage({ title: "Generating Files", message: "Generating Measure Layout..." });
                const measureLayoutPdf = await generateMeasureLayout(sceneViewer, viewerState);
                if (measureLayoutPdf) {
                    const fileName = `${siteAddress} - MEASURE LAYOUT.pdf`;
                    const measureBuffer = measureLayoutPdf.output("arraybuffer") as ArrayBuffer;
                    const measureBlob = new Blob([measureBuffer], { type: "application/pdf" });
                    files.push({ data: measureBlob, name: fileName });
                }
            }

            /** Site Report */
            if (hasCapability("oneDRAW_Site_Report") || hasCapability("Genesis_Site_Report")) {
                loadingContext.setMessage({ title: "Generating Files", message: "Generating Site Report..." });
                const siteReportPdf = await generateSiteReport(sceneViewer, viewerState, unitContext);
                if (siteReportPdf) {
                    const fileName = `${siteAddress} - SITE REPORT.pdf`;
                    // @ts-ignore
                    files.push({ data: siteReportPdf.output("blob", fileName), name: fileName });
                }
            }

            /** Genesis File */
            if (hasCapability(".GEN")) {
                loadingContext.setMessage({ title: "Generating Files", message: "Generating Genesis File..." });
                const exportGen = await ExportGen(sceneViewer, false);
                if (exportGen) {
                    const fileName = `${siteAddress}.genesis`;
                    files.push({ data: exportGen as Blob, name: fileName });
                }
            }

            /** Screenshot */
            if (hasCapability("Screenshot")) {
                loadingContext.setMessage({ title: "Generating Files", message: "Generating Topview..." });
                const images = await sceneViewer.getImagesForSiteReport("home");
                const fileName = `${siteAddress}_topview.png`;
                files.push({ data: await fetch(images.home).then((response) => response.blob()), name: fileName });
            }
        } catch (error) {
            logger.error(error);
        } finally {
            loadingContext.dispose();
        }
        return files;
    };

    const uploadAllFiles = async (
        salesforceId: string | null,
        files: Array<{ data: Blob; name: string }>,
        onComplete: (uploaded: string[], failed: string[]) => void,
        onError: (error: unknown) => void,
    ) => {
        const uploadedFiles: string[] = [];
        const notUploaded: string[] = [];
        const loadingContext = new LoadingContext();
        loadingContext.setDefaultTitle("Uploading Files...");

        try {
            const promises = files.map(async (file, idx) => {
                loadingContext.setMessage({
                    title: `Uploading to Box (${idx + 1}/${files.length})`,
                    message: `Uploading: ${file.name} (0%)`,
                });
                return uploadToBox(salesforceId as string, file, (progress: number) => {
                    loadingContext.setMessage({
                        title: `Uploading to Box (${idx + 1}/${files.length})`,
                        message:
                            progress === 100
                                ? `Processing: '${file.name}'`
                                : `Uploading: ${file.name} (${progress}%)`,
                    });
                });
            });

            const results = await Promise.all(promises);
            results.forEach((result, idx) => {
                if (result) uploadedFiles.push(files[idx].name);
                else notUploaded.push(files[idx].name);
            });
        } catch (error) {
            onError(error);
        } finally {
            useViewerStore.setState({ isSavingInProgress: false });
            loadingContext.dispose();
            onComplete(uploadedFiles, notUploaded);
        }
    };

    const saveEpcEstimate = (DesignNumber: string, VersionNumber: string) => {
        const sales_force_id = sessionStorage.getItem("SalesForceId");
        const { pricingType, ppwValue, multiplier, escalatorValue, epcValue, epcMessage } =
            useViewerStore.getState();

        const payload = {
            salesforce_Id: sales_force_id,
            pricing_Method: pricingType,
            pricing_Value: ppwValue,
            annual_Usage: annualUsageInput,
            multiplier,
            escalator: escalatorValue,
            design_Data: `Design:${DesignNumber}-Version:${VersionNumber}`,
            epC_PriceValue: epcValue,
            estimated_Production: epcEstimatedProductionInput,
            exception_Message: epcMessage,
        };

        return treeEPCService.saveEpcEstimation(payload);
    };


    return (
        <>
            {notReady ? (
                <>
                    <Alert style={{ color: "black" }} message="Please wait, values are still being calculated" type="info" showIcon />
                    <br />
                    <Flex style={{ textAlign: "center" }} gap={10} align="center" justify="center">
                        <span className="loader-icon-spinner " />
                        <p className="theme-based-text-color">
                            Re-calculating shades on roof {shadeCalculatingIdx} of {shadeCalculatingLength}...
                        </p>
                    </Flex>
                </>
            ) : saving ? (
                <>
                    <Alert
                        style={{ color: "black" }}
                        message="This may take up to 30 seconds, don't refresh or quit while in progress."
                        type="info"
                        showIcon
                    />
                    <br />
                    <Flex style={{ textAlign: "center" }} gap={10} align="center" justify="center">
                        <span className="loader-icon-spinner " />
                        <p className="theme-based-text-color">
                            Saving New {designID === 0 || isInActiveDesign ? "Design" : "Version"} to the database. Please Wait...
                        </p>
                    </Flex>
                </>
            ) : (
                <>
                    <b>
                        <p className="report-text">
                            Are you sure you want to Finalize and Save the Design. Click Confirm to proceed.
                        </p>
                    </b>
                    <br />
                    <ul className="report-list" style={{ width: "550px" }}>
                        <li style={{ fontWeight: "bold" }}>Setback Notes:</li>
                        <li>{setbackNotes}</li>
                    </ul>
                    <br />

                    {
                        detachedStructureBuildingIds.length > 0 &&
                        <p>
                            <b>Note:</b> Following buildings are marked as Detached Structures:
                            <br/>
                            {
                                detachedStructureBuildingIds.map((buildingId : number,index) => (
                                    <span> Building {buildingId}{detachedStructureBuildingIds.length>1 && ","}</span>
                                ))
                            }
                        </p>
                    }

                    {isInActiveDesign ? (
                        <Alert
                            style={{ color: "black" }}
                            message="You are Re-finalizing an Inactive Design / Older Design . This will be treated as a New Design and will not be saved as a New Version."
                            type="warning"
                            showIcon
                        />
                    ) : null}
                    <br />

                    <div style={{ display: "flex", flexDirection: "column", marginBottom: "15px", width: "300px" }}>
                        {hasCapability("RSA_Status") && (
                            <div className="report-flex" style={{ marginTop: "10px" }}>
                                <b>
                                    <p className="report-text">RSA status:</p>
                                </b>
                                <Select
                                    placeholder="RSA Status"
                                    options={rsaStatusOptions}
                                    onChange={setRsaStatus}
                                    size="large"
                                    suffixIcon={<CaretDown size={18} />}
                                />
                            </div>
                        )}
                        {hasCapability("Direct_Layout") && (
                            <>
                                <div className="report-flex" style={{ marginTop: "15px" }}>
                                    <b>
                                        <p className="report-text">Annual usage:</p>
                                    </b>
                                    <Input
                                        type="number"
                                        value={annualUsageInput ?? undefined}
                                        onChange={(e) => setAnnualUsageInput(Number(e.target.value))}
                                        placeholder="Enter Value Here in kWh"
                                    />
                                </div>
                                <div className="report-flex" style={{ marginTop: "15px" }}>
                                    <b>
                                        <p className="report-text">Finance partner production:</p>
                                    </b>
                                    <Input
                                        type="number"
                                        value={epcEstimatedProductionInput ?? undefined}
                                        onChange={(e) => setEpcEstimatedProductionInput(Number(e.target.value))}
                                        placeholder="Enter Value Here in kWh"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="modal-bottom-action-button-container">
                        <Button
                            id="finaliseConfirmBtn"
                            type="primary"
                            size="large"
                            className="editor__control--finalize modal-confirm-button"
                            onClick={confirm}
                            disabled={hasCapability("RSA_Status") && !rsaStatus}
                        >
                            Confirm
                        </Button>

                        <Button
                            type="primary"
                            size="large"
                            danger
                            className="editor__control--finalize modal-cancel-button"
                            onClick={cancel}
                        >
                            Cancel
                        </Button>
                    </div>
                </>
            )}
        </>
    );
};

export default FinaliseConfirm;
